import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";
import { StarRating } from "../components/StarRating";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { invalidateFavoritesCache } from "../components/FavoriteButton";
import { useSeo } from "../lib/seo";
import BookingAnswersView from "../components/BookingAnswersView";
import MyBookingRequests from "../components/MyBookingRequests";

const CHIPS = ["Cleaning", "Beauty", "Home Repair", "Photography", "Detailing", "Private Chef", "Fitness", "Pet Care", "Tutoring", "Auto"];

const STATUS_META = {
  pending: { label: "Awaiting business reply", tone: "warn" },
  pending_payment: { label: "Awaiting payment", tone: "warn" },
  payment_required: { label: "Payment issue — update card", tone: "bad" },
  confirmed: { label: "Confirmed", tone: "ok" },
  completed: { label: "Completed", tone: "muted" },
  declined: { label: "Declined by business", tone: "bad" },
  cancelled: { label: "Cancelled", tone: "muted" },
  refunded: { label: "Refunded", tone: "muted" },
};

const TONE_STYLE = {
  ok: { borderColor: "var(--gf-surface)", color: "var(--gf-accent)", background: "var(--gf-surface)" },
  warn: { borderColor: "var(--gf-surface-2)", color: "var(--gf-accent)", background: "var(--gf-surface-2)" },
  muted: { borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)", background: "transparent" },
  bad: { borderColor: "rgba(180,60,60,0.4)", color: "rgb(180,60,60)", background: "rgba(180,60,60,0.05)" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: "muted" };
  const s = TONE_STYLE[meta.tone];
  return (
    <span
      data-testid={`status-pill-${status}`}
      className="text-[10px] uppercase tracking-[0.2em] border rounded-full px-2 py-1 whitespace-nowrap"
      style={s}
    >
      {meta.label}
    </span>
  );
}

const BALANCE_STATES = {
  card_saved: { label: "Card saved", tone: "muted" },
  scheduled: { label: "Card saved", tone: "muted" },
  authorizing: { label: "Processing", tone: "muted" },
  authorized: { label: "Authorization hold placed", tone: "muted" },
  hold_released: { label: "Hold released", tone: "muted" },
  processing: { label: "Processing", tone: "muted" },
  captured: { label: "Paid", tone: "accent" },
  payment_required: { label: "Payment required", tone: "warn" },
  refunded: { label: "Refunded", tone: "muted" },
  in_person: { label: "Settled in person", tone: "muted" },
};

function FinalPaymentState({ b }) {
  const [busy, setBusy] = useState(false);
  const meta = BALANCE_STATES[b.remaining_balance_status] || { label: "Card saved", tone: "muted" };
  const tone = meta.tone === "accent"
    ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)" }
    : meta.tone === "warn"
      ? { border: "1px solid var(--gf-accent)", color: "var(--gf-accent)" }
      : { border: "1px solid var(--gf-border-strong)", color: "var(--gf-text-soft)" };

  const payNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bookings/${b.id}/balance/pay-link`,
        { origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${localStorage.getItem("gf_token")}` } });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the payment page");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap text-sm" data-testid={`final-payment-${b.id}`}>
      <span className="rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.15em]"
            data-testid={`final-payment-state-${b.id}`} style={tone}>
        {meta.label}
      </span>
      {b.saved_card_brand && b.saved_card_last4 && (
        <span style={{ color: "var(--gf-text-muted)" }} data-testid={`saved-card-${b.id}`}>
          {b.saved_card_brand.toUpperCase()} ···· {b.saved_card_last4}
        </span>
      )}
      {b.remaining_balance_status === "payment_required" && (
        <button type="button" onClick={payNow} disabled={busy} data-testid={`pay-balance-btn-${b.id}`}
                className="rounded-full px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
          {busy ? "Opening…" : "Complete payment"}
        </button>
      )}
    </div>
  );
}

export default function CustomerDashboard() {
  useSeo({ title: "My account — BookFreely", path: "/account", noindex: true });
  const { user, authHeader, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    // Both customer and business roles can view the Customer dashboard — a
    // business owner is a unified account and may want to see their personal
    // bookings ("Customer view"). Only admin/super_admin get bounced.
    if (user.role !== "customer" && user.role !== "business") { nav("/"); return; }
    refresh();
    axios.get(`${API}/services`).then((r) => setDiscover(r.data.slice(0, 6))).catch(() => {});
    // eslint-disable-next-line
  }, [loading, user?.id]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/bookings/customer`, { headers: authHeader() });
      setBookings(r.data);
    } catch {}
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?\n\nIf you paid a refundable deposit, contact the business directly to coordinate the refund — BookFreely does not move money.")) return;
    try {
      await axios.post(`${API}/bookings/${id}/action`, { action: "cancel" }, { headers: authHeader() });
      toast.success("Booking cancelled");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const { upcoming, past, cancelled } = useMemo(() => {
    const withD = bookings.map((b) => ({ ...b, _d: new Date(b.scheduled_at) }));
    const sortAsc = (a, b) => a._d - b._d;
    const sortDesc = (a, b) => b._d - a._d;
    return {
      // Feb 27 2026 — `pending_payment` bookings are ghosts of an
      // in-flight Stripe Checkout that hasn't been confirmed yet. Backend
      // already excludes them from /bookings/customer, but we belt-and-brace
      // filter here so an older API response can never leak them into the
      // Upcoming tab. Users should NEVER see a booking they didn't pay for.
      upcoming: withD.filter((b) => ["pending", "confirmed", "payment_required"].includes(b.status)).sort(sortAsc),
      past: withD.filter((b) => b.status === "completed").sort(sortDesc),
      cancelled: withD.filter((b) => ["cancelled", "declined", "refunded"].includes(b.status)).sort(sortDesc),
    };
  }, [bookings]);

  const tabs = [
    { id: "upcoming", label: "Upcoming", count: upcoming.length },
    { id: "past", label: "Past", count: past.length },
    { id: "cancelled", label: "Cancelled", count: cancelled.length },
    { id: "favorites", label: "Favorites", count: null },
  ];
  const items = tab === "upcoming" ? upcoming : tab === "past" ? past : tab === "cancelled" ? cancelled : [];

  if (loading || !user) return <div className="App"><Header hideSearch /><div className="max-w-5xl mx-auto p-12" style={{ color: "var(--gf-text-muted)" }}>Loading...</div></div>;

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Account</div>
        <div className="flex items-start justify-between gap-4 flex-wrap mt-2">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl" style={{ color: "var(--gf-text)" }}>
              Welcome back, {user.full_name.split(" ")[0]}
            </h1>
            <p className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>
              Manage your bookings, request changes, and discover more services.
            </p>
          </div>
          {user.role === "business" && (
            <button
              onClick={() => nav("/business")}
              className="rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] hover:opacity-80 shrink-0"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              data-testid="switch-to-business-btn"
            >
              Switch to business view →
            </button>
          )}
        </div>

        {user.role === "customer" && (
          <section
            className="mt-8 rounded-2xl border p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4"
            style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
            data-testid="own-a-business-card"
          >
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-accent)" }}>Own a business?</div>
              <div className="font-display text-lg sm:text-xl mt-1" style={{ color: "var(--gf-text)" }}>
                Add your business to BookFreely
              </div>
              <p className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
                Start accepting bookings using your existing account. Your bookings, favourites and reviews stay put.
              </p>
            </div>
            <button
              onClick={() => nav("/account/add-business")}
              className="rounded-full px-5 py-2 text-sm font-medium shrink-0"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              data-testid="add-your-business-btn"
            >
              Add Your Business
            </button>
          </section>
        )}

        <section className="mt-8">
          <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>My appointments</h2>

          <div className="mt-4 flex gap-2 border-b overflow-x-auto" style={{ borderColor: "var(--gf-border)" }} data-testid="appointment-tabs">
            {tabs.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="px-4 py-2.5 text-sm flex items-center gap-2 transition-colors relative whitespace-nowrap"
                  style={{
                    color: active ? "var(--gf-text)" : "var(--gf-text-muted)",
                    borderBottom: active ? "2px solid var(--gf-accent)" : "2px solid transparent",
                    marginBottom: "-1px",
                  }}
                >
                  {t.label}
                  {t.count != null && (
                    <span
                      className="text-[10px] rounded-full px-1.5 py-0.5"
                      style={{
                        background: active ? "var(--gf-accent)" : "var(--gf-bg-soft)",
                        color: active ? "var(--gf-on-accent)" : "var(--gf-text-muted)",
                      }}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {tab === "upcoming" && (
            <div className="mt-5">
              <MyBookingRequests authHeader={authHeader} />
            </div>
          )}

          <div className="mt-5 space-y-3" data-testid={`appointments-${tab}`}>
            {tab === "favorites" ? (
              <FavoritesPanel authHeader={authHeader} />
            ) : items.length === 0 ? (
              <EmptyState tab={tab} />
            ) : items.map((b) => (
              <AppointmentCard
                key={b.id}
                booking={b}
                tab={tab}
                authHeader={authHeader}
                onCancel={() => cancel(b.id)}
                onReschedule={() => setRescheduleFor(b)}
              />
            ))}
          </div>
        </section>

        <section className="mt-14 sm:mt-16">
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Discover</div>
          <h2 className="font-display text-2xl sm:text-3xl mt-2" style={{ color: "var(--gf-text)" }}>Book another service</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <button
                key={c}
                data-testid={`discover-chip-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => nav(`/services?category=${encodeURIComponent(c)}`)}
                className="text-xs rounded-full border px-3 py-1.5"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                {c}
              </button>
            ))}
          </div>
          {discover.length > 0 && (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="discover-grid">
              {discover.map((s) => <ServiceCard key={s.id} s={s} />)}
            </div>
          )}
          <div className="mt-6">
            <Link to="/services" data-testid="browse-all-link" className="rounded-full font-medium px-6 py-3 inline-block" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Browse all services →
            </Link>
          </div>
        </section>
      </div>

      {rescheduleFor && (
        <RescheduleModal
          booking={rescheduleFor}
          onClose={() => setRescheduleFor(null)}
          onSent={() => { setRescheduleFor(null); toast.success("Reschedule request sent — the business will reply in your messages."); }}
          authHeader={authHeader}
        />
      )}

      <Footer />
    </div>
  );
}

function EmptyState({ tab }) {
  const copy = {
    upcoming: { title: "No upcoming appointments", body: "Find a service below to book your next appointment." },
    past: { title: "No past appointments", body: "Once a business marks an appointment complete, it'll show up here." },
    cancelled: { title: "Nothing here", body: "Cancelled, declined, and refunded bookings will appear here." },
  }[tab];
  return (
    <div className="rounded-2xl border p-8 text-center" style={{ borderColor: "var(--gf-border)" }}>
      <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>{copy.title}</div>
      <p className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>{copy.body}</p>
    </div>
  );
}

function FavoritesPanel({ authHeader }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ businesses: [], services: [] });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/favorites`, { headers: authHeader() });
      setData({ businesses: r.data.businesses || [], services: r.data.services || [] });
    } catch {
      setData({ businesses: [], services: [] });
    }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);
  // Real-time-ish sync: refetch bookings every 30s (Stripe webhook → booking
  // confirmed → this poll picks it up ≤ 30s later) AND immediately when the
  // user brings the tab back into focus (usually returning from Stripe Checkout).
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 30000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(t); };
  }, [load]);

  const unfavoriteBiz = async (id) => {
    try {
      await axios.delete(`${API}/favorites/business/${id}`, { headers: authHeader() });
      invalidateFavoritesCache();
      setData((d) => ({ ...d, businesses: d.businesses.filter((b) => b.id !== id) }));
      toast.success("Removed from favorites");
    } catch (e) { toast.error("Could not remove"); }
  };
  const unfavoriteSvc = async (id) => {
    try {
      await axios.delete(`${API}/favorites/service/${id}`, { headers: authHeader() });
      invalidateFavoritesCache();
      setData((d) => ({ ...d, services: d.services.filter((s) => s.id !== id) }));
      toast.success("Removed from favorites");
    } catch (e) { toast.error("Could not remove"); }
  };

  if (loading) return <div style={{ color: "var(--gf-text-muted)" }}>Loading your favorites...</div>;

  const nothing = data.businesses.length === 0 && data.services.length === 0;
  if (nothing) {
    return (
      <div className="rounded-2xl border p-8 text-center" data-testid="favorites-empty" style={{ borderColor: "var(--gf-border)" }}>
        <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>No favorites yet</div>
        <p className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>
          Tap the heart on any business or service to save it here.
        </p>
        <Link to="/services" className="inline-block mt-4 rounded-full px-5 py-2 text-sm font-medium" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
          Browse services
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="favorites-panel">
      {data.businesses.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "var(--gf-text-muted)" }}>
            Saved businesses · {data.businesses.length}
          </div>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="favorites-businesses">
            {data.businesses.map((b) => <FavBusinessCard key={b.id} b={b} onRemove={() => unfavoriteBiz(b.id)} />)}
          </div>
        </div>
      )}
      {data.services.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "var(--gf-text-muted)" }}>
            Saved services · {data.services.length}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="favorites-services">
            {data.services.map((s) => (
              <div key={s.id} className="relative">
                <ServiceCard s={s} />
                <button
                  type="button"
                  onClick={() => unfavoriteSvc(s.id)}
                  data-testid={`fav-service-remove-${s.id}`}
                  className="absolute bottom-3 right-3 z-10 text-[11px] rounded-full px-3 py-1.5"
                  style={{ background: "rgba(255,255,255,0.95)", color: "var(--gf-text-muted)", border: "1px solid var(--gf-border-strong)" }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FavBusinessCard({ b, onRemove }) {
  return (
    <div className="rounded-xl border p-4 flex items-start gap-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid={`fav-biz-${b.id}`}>
      {b.profile_image_url ? (
        <img src={b.profile_image_url} alt={b.business_name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-display text-xl"
             style={{ background: "var(--gf-surface-2)", color: "var(--gf-text-muted)" }}>
          {(b.business_name || "?").charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/business/${b.id}`} className="font-display text-lg leading-tight hover:underline truncate" style={{ color: "var(--gf-text)" }}>
            {b.business_name}
          </Link>
          {b.is_verified && (
            <span className="text-[9px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Verified
            </span>
          )}
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
          {b.business_category || "Service business"}
          {(b.city || b.state) && <> · {[b.city, b.state].filter(Boolean).join(", ")}</>}
        </div>
        <div className="mt-1.5">
          <StarRating value={b.avg_rating || 0} count={b.review_count || 0} size={13} />
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Link
            to={`/business/${b.id}`}
            data-testid={`fav-biz-book-${b.id}`}
            className="rounded-full px-4 py-1.5 text-xs font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            Quick book
          </Link>
          <button
            type="button"
            data-testid={`fav-biz-remove-${b.id}`}
            onClick={onRemove}
            className="rounded-full border px-3 py-1.5 text-xs"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function AppointmentCard({ booking: b, tab, onCancel, onReschedule, authHeader }) {
  const hasDeposit = Number(b.downpayment_amount || 0) > 0;
  const canManage = tab === "upcoming";
  const canReview = b.status === "completed";
  return (
    <div className="rounded-xl border p-4 sm:p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid={`appt-${b.id}`}>
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] truncate" style={{ color: "var(--gf-text-muted)" }}>{b.business_name}</div>
          <Link to={`/services/${b.service_id}`} className="font-display text-lg sm:text-xl mt-1 hover:underline block truncate" style={{ color: "var(--gf-text)" }} data-testid={`appt-title-${b.id}`}>
            {b.service_title}
          </Link>
          <div className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
            {b._d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            {b.duration_minutes ? <span style={{ color: "var(--gf-text-muted)" }}> · {b.duration_minutes} min</span> : null}
          </div>
        </div>
        <StatusPill status={b.status} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-sm rounded-lg border p-3" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Deposit paid</div>
          <div className="mt-0.5" style={{ color: "var(--gf-text)" }} data-testid={`appt-deposit-${b.id}`}>
            ${Number(b.downpayment_amount || 0).toFixed(2)}
            {hasDeposit && <span className="text-xs ml-1" style={{ color: "var(--gf-text-muted)" }}>({b.downpayment_refundable ? "refundable" : "non-refundable"})</span>}
          </div>
        </div>
        <div className="text-right sm:text-left">
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
            {b.balance_charge_mode === "on_completion" ? "Card charged after appointment" : "Due at appointment"}
          </div>
          <div className="mt-0.5" style={{ color: "var(--gf-text)" }} data-testid={`appt-balance-${b.id}`}>
            ${Number(b.balance_due_at_appointment != null ? b.balance_due_at_appointment : (b.service_price_snapshot || 0) - (b.downpayment_amount || 0)).toFixed(2)}
          </div>
        </div>
      </div>

      {b.balance_charge_mode === "on_completion" && (
        <FinalPaymentState b={b} />
      )}

      {b.notes && (
        <div className="text-sm mt-3 italic" style={{ color: "var(--gf-text-muted)" }}>
          "{b.notes}"
        </div>
      )}

      <BookingAnswersView answers={b.booking_answers} authHeader={authHeader} testId={`appt-answers-${b.id}`} />

      {b.is_mobile && (b.status === "confirmed" || b.status === "completed") && (
        <MobileSafetyBlock b={b} authHeader={authHeader} />
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {canManage && (
          <>
            <button
              data-testid={`reschedule-${b.id}`}
              onClick={onReschedule}
              className="text-xs rounded-full border px-3 py-1.5"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Request reschedule
            </button>
            <Link
              to={`/business/${b.business_id}`}
              data-testid={`message-${b.id}`}
              className="text-xs rounded-full border px-3 py-1.5"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Message business
            </Link>
            <button
              data-testid={`cancel-${b.id}`}
              onClick={onCancel}
              className="text-xs rounded-full border px-3 py-1.5 ml-auto"
              style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}
            >
              Cancel booking
            </button>
          </>
        )}
        {canReview && (
          <Link
            to={`/services/${b.service_id}`}
            data-testid={`review-${b.id}`}
            className="text-xs rounded-full px-3 py-1.5 font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            Leave a review →
          </Link>
        )}
      </div>
    </div>
  );
}

function RescheduleModal({ booking, onClose, onSent, authHeader }) {
  const [preferred, setPreferred] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!preferred && note.trim().length < 5) {
      toast.error("Please share a preferred time or a short note");
      return;
    }
    setSending(true);
    try {
      const payload = {
        preferred_time: preferred ? new Date(preferred).toISOString() : "",
        note,
      };
      await axios.post(`${API}/bookings/${booking.id}/reschedule-request`, payload, { headers: authHeader() });
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose} data-testid="reschedule-modal">
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[90vh] overflow-y-auto" style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Reschedule</div>
        <h3 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>Request a new time</h3>
        <p className="text-xs mt-2" style={{ color: "var(--gf-text-muted)" }}>
          We'll send your request to <b style={{ color: "var(--gf-text)" }}>{booking.business_name}</b> as a message — they'll reply to confirm a new time. Your current booking stays as-is until they respond.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Preferred new time</label>
            <input
              data-testid="reschedule-time-input"
              type="datetime-local"
              value={preferred}
              onChange={(e) => setPreferred(e.target.value)}
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Note (optional)</label>
            <textarea
              data-testid="reschedule-note-input"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              placeholder="Anything the business should know about why you need to reschedule..."
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button
            data-testid="reschedule-cancel-btn"
            onClick={onClose}
            className="text-sm rounded-full border px-4 py-2"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}
          >
            Cancel
          </button>
          <button
            data-testid="reschedule-send-btn"
            onClick={send}
            disabled={sending}
            className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            {sending ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TIMELINE_STEPS = [
  { key: "confirmed", label: "Booking confirmed" },
  { key: "on_the_way", label: "On the way" },
  { key: "arrived", label: "Arrived" },
  { key: "in_progress", label: "In progress" },
  { key: "completed", label: "Completed" },
];

function MobileSafetyBlock({ b, authHeader }) {
  const [pin, setPin] = useState(b.customer_pin || null);
  const [pinBusy, setPinBusy] = useState(false);
  const [checkinBusy, setCheckinBusy] = useState(false);
  const status = b.provider_status || (b.status === "completed" ? "completed" : "confirmed");
  const currentIdx = TIMELINE_STEPS.findIndex((s) => s.key === status);

  const fetchPin = async () => {
    setPinBusy(true);
    try {
      const r = await axios.get(`${API}/bookings/${b.id}/pin`, { headers: authHeader() });
      setPin(r.data.pin);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN not available yet");
    } finally {
      setPinBusy(false);
    }
  };

  useEffect(() => {
    if (!pin && b.status === "confirmed") fetchPin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkin = async (arrived) => {
    let note = "";
    if (!arrived) {
      note = window.prompt("Briefly describe what happened (sent to BookFreely safety team):") || "";
      if (!note.trim()) return;
    }
    setCheckinBusy(true);
    try {
      await axios.post(`${API}/bookings/${b.id}/customer-checkin`, { arrived_correctly: arrived, note }, { headers: authHeader() });
      toast.success(arrived ? "Thanks for confirming" : "Reported — our team will follow up");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setCheckinBusy(false);
    }
  };

  return (
    <div data-testid={`mobile-safety-${b.id}`} className="mt-4 rounded-xl border p-4"
         style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg)" }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {b.business_mobile_verified && (
            <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Mobile-safe
            </span>
          )}
          <span className="text-sm font-medium" style={{ color: "var(--gf-text)" }}>Mobile / in-home safety</span>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-3 grid grid-cols-5 gap-1">
        {TIMELINE_STEPS.map((s, i) => (
          <div key={s.key} className="flex flex-col items-center text-center">
            <div className="w-2.5 h-2.5 rounded-full"
                 style={{ background: i <= currentIdx ? "var(--gf-accent)" : "var(--gf-border-strong)" }} />
            <div className="mt-1 text-[10px] leading-tight" style={{ color: i <= currentIdx ? "var(--gf-text)" : "var(--gf-text-muted)" }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* PIN */}
      <div className="mt-4 rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Your appointment PIN</div>
          <div className="font-display text-3xl tracking-[0.4em] mt-1" style={{ color: "var(--gf-text)" }} data-testid={`pin-${b.id}`}>
            {pin || "······"}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "var(--gf-text-soft)" }}>Read this to your provider on arrival. They'll confirm it in their app before starting.</div>
        </div>
        {!pin && (
          <button onClick={fetchPin} disabled={pinBusy} data-testid={`pin-show-${b.id}`}
                  className="rounded-md px-3 py-1.5 text-xs border"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            {pinBusy ? "…" : "Show PIN"}
          </button>
        )}
      </div>

      {/* Customer check-in */}
      {(status === "arrived" || b.pin_verified_at) && !b.customer_checkin && (
        <div className="mt-4 rounded-lg border p-3" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }}>
          <div className="text-sm font-medium" style={{ color: "var(--gf-text)" }}>Did the correct verified provider arrive?</div>
          <div className="mt-2 flex gap-2">
            <button data-testid={`checkin-yes-${b.id}`} onClick={() => checkin(true)} disabled={checkinBusy}
                    className="rounded-md px-4 py-2 text-sm" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {checkinBusy ? "…" : "Yes"}
            </button>
            <button data-testid={`checkin-no-${b.id}`} onClick={() => checkin(false)} disabled={checkinBusy}
                    className="rounded-md px-4 py-2 text-sm border"
                    style={{ borderColor: "var(--gf-danger)", color: "var(--gf-danger)" }}>
              No / Report
            </button>
          </div>
        </div>
      )}
      {b.customer_checkin && (
        <div className="mt-3 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
          Check-in submitted: <b style={{ color: b.customer_checkin === "yes" ? "var(--gf-success)" : "var(--gf-danger)" }}>{b.customer_checkin === "yes" ? "Yes" : "Reported"}</b>
        </div>
      )}

      <div className="mt-3 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
        Privacy: Your contact info is only shared with the assigned provider for this confirmed booking. Your full address becomes visible to them within 2 hours of the appointment.
      </div>
    </div>
  );
}

